import { ANALYTICS_TYPE, LAYER_PLACEMENT, VIEW } from '../../enums';
import { AcceptAllButton, CustomizeButton, DenyAllButton, SaveButton } from '../Buttons';
import { Fragment, FunctionalComponent, h } from 'preact';
import Usercentrics, {
  TCFUISettings,
  TCF_DECISION_UI_LAYER,
  TCF_SCOPE,
  TRACKING_EVENT_TYPE,
} from '@usercentrics/cmp-browser-sdk';
import { dispatchAnalyticsEvent, setTrackingPixel } from '../../modules/helpers';
import { getTCFUserDecision, getUserDecision } from '../../App.helpers';
import { useDeviceState, useMutation } from '../../hooks';
import MainLayout from '../../components/MainLayout/MainLayout';
import { TCFButtons } from '../../components/ButtonsContainer/interface';
import TCFFirstLayerContent from './TCFFirstLayerContent';
import TCFTabs from './TCFTabs';
import { Tabs } from '../../components/Tabs';
import { memo } from 'preact/compat';
import { useCategoryContext } from '../../contexts/CategoryContext';
import { useEffect } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useTcfContext } from '../../contexts/TcfContext';
import { useUiContext } from '../../contexts/UiContext';

const testIds = {
  firstLayer: 'uc-tcf-first-layer',
  secondLayer: 'uc-tcf-second-layer',
};
interface Props {
  onCustomize: () => void;
  onFullScreenAmp: () => void;
  onLanguageChange: (selectedLanguage: string) => void;
  onShowVendorsHandler: () => void;
  onAcceptAll: () => Promise<void>;
  onDenyAll: () => Promise<void>;
  isAcceptDenyAllLoading: boolean;
  UC: Usercentrics;
}

const TCF: FunctionalComponent<Props> = ({
  onCustomize,
  onFullScreenAmp,
  onLanguageChange,
  onShowVendorsHandler,
  onAcceptAll,
  onDenyAll,
  isAcceptDenyAllLoading,
  UC,
}) => {
  const { settings } = useSettingsContext();
  const { isIE } = useDeviceState();
  const { closeView, isAmpEnabled, isInFullScreen, previousView, setIsInFullScreen, settingsCloseView, setView, view } =
    useUiContext();
  const { categories, getUpdatedCategories, setCategories } = useCategoryContext();
  const { tcfData, setTcfData } = useTcfContext();
  const ui = settings.ui as TCFUISettings;
  const [dismissAmp, { loading: dismissAmpLoading }] = useMutation(UC.dismissAmp, UC);
  const [saveTCFDataAmp, { loading: saveTcfDataAmpLoading }] = useMutation(UC.saveTCFDataAmp, UC);
  const [updateServices, { loading: updateServicesLoading }] = useMutation(UC.updateServices, UC);
  const [updateChoicesForTCF, { loading: updateChoicesForTCFLoading }] = useMutation(UC.updateChoicesForTCF, UC);

  /**
   * This method should always be triggered when clicking on the AcceptAll button on TCF UI.
   * @param fromLayer from which layer the decision was made
   */
  const onAcceptAllHandler = (): void => {
    onAcceptAll().then(() => {
      closeView();
    });
  };

  /**
   * This method should always be triggered when clicking on the DenyAll button.
   * @param fromLayer from which layer the decision was made
   */
  const onDenyAllHandler = (): void => {
    onDenyAll().then(() => {
      closeView();
    });
  };

  useEffect(() => {
    setTcfData(UC.getTCFData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (view === VIEW.FIRST_LAYER || view === VIEW.SECOND_LAYER) &&
      (previousView === VIEW.PRIVACY_BUTTON || previousView === VIEW.NONE)
    ) {
      UC.setTCFUIAsOpen();
    }
  }, [UC, previousView, view]);

  const onCloseAmp = (): void => {
    if (dismissAmpLoading) {
      return;
    }

    dismissAmp().then(() => {
      setIsInFullScreen(false);
      closeView();
    });
  };

  /**
   * This method should always be triggered when clicking on close button on the second layer of TCF UI.
   */
  const onCloseTCFHandler = (): void => {
    if (isAmpEnabled && (settingsCloseView === VIEW.NONE || settingsCloseView === VIEW.PRIVACY_BUTTON)) {
      dismissAmp();
    }

    // The settingsCloseView always provides a reference to the view that the secondLayer was opened from. If the
    // secondLayer was opened from the firstLayer, then we will go back to the firstLayer (the user has to make a
    // choice!). If the secondLayer was opened from the Privacy Button / using a button from the host website, we will
    // go back to the Privacy Button View / no view respectively.

    if (
      (previousView === VIEW.FIRST_LAYER || previousView === VIEW.SECOND_LAYER) &&
      (settingsCloseView === VIEW.NONE || settingsCloseView === VIEW.PRIVACY_BUTTON)
    ) {
      UC.setTCFUIAsClosed();
    }
    setView(settingsCloseView);
  };

  /**
   * This method should always be triggered when clicking on the Save button from TCF UI.
   * @param fromLayer from which layer the decision was made
   */
  const onSaveTCFHandler = (): void => {
    if (!tcfData || updateServicesLoading || updateChoicesForTCFLoading || dismissAmpLoading || saveTcfDataAmpLoading) {
      return;
    }

    const userDecisions = getUserDecision(categories);
    const tcfUserDecisions = getTCFUserDecision(tcfData);
    const fromLayer =
      view === VIEW.FIRST_LAYER ? TCF_DECISION_UI_LAYER.FIRST_LAYER : TCF_DECISION_UI_LAYER.SECOND_LAYER;
    // When TCF is enabled, we need to wait for both the services and the vendors/purposes to be updated before continuing.
    Promise.all([updateServices(userDecisions), updateChoicesForTCF(tcfUserDecisions, fromLayer)]).then(async () => {
      if (isAmpEnabled) {
        await saveTCFDataAmp(userDecisions);
        await dismissAmp();
      }
      dispatchAnalyticsEvent(view, ANALYTICS_TYPE.SAVE);
      setTrackingPixel(TRACKING_EVENT_TYPE.CUSTOM_CHOICE, UC.setTrackingPixel, settings.interactionAnalyticsEnabled);
      setCategories(await getUpdatedCategories(view));
      setTcfData(UC.getTCFData());
      closeView();
    });
  };

  const loadingMutation =
    updateServicesLoading || updateChoicesForTCFLoading || dismissAmpLoading || isAcceptDenyAllLoading;

  const acceptAll = <AcceptAllButton disabled={loadingMutation} onClick={onAcceptAllHandler} />;
  const denyAll = <DenyAllButton disabled={loadingMutation} onClick={onDenyAllHandler} />;
  const customize = <CustomizeButton disabled={loadingMutation} onClick={onCustomize} />;
  const save = <SaveButton disabled={loadingMutation} onClick={onSaveTCFHandler} />;

  const firstLayerDescription = `${ui.firstLayer.description.default}
  ${ui.firstLayer.description.additionalInfo ? ui.firstLayer.description.additionalInfo : ''}
  ${ui.firstLayer.description.resurfaceNote ? ui.firstLayer.description.resurfaceNote : ''}
  ${ui.scope === TCF_SCOPE.GLOBAL ? ui.firstLayer.disclaimer.globalScope : ''}
  `;

  const buttonsToShowInFirstLayer: TCFButtons = {
    AcceptAllButton: acceptAll,
    ...(!ui.firstLayer.hideButtonDeny && { DenyAllButton: denyAll }),
    SaveButton: ui.firstLayer.hideToggles ? customize : save,
  };

  const buttonsToShowInSecondLayer: TCFButtons = {
    AcceptAllButton: acceptAll,
    ...(!ui.secondLayer.hideButtonDeny && { DenyAllButton: denyAll }),
    SaveButton: save,
  };

  if (!tcfData) {
    return <Fragment />;
  }

  /**
   * SWITCH VIEW - Decision logic for showing the correct view and variant.
   */
  switch (view) {
    case VIEW.FIRST_LAYER:
      return (
        <MainLayout
          customActions={[
            {
              ariaLabel: ui.buttons.showVendorTab.label,
              label: ui.buttons.showVendorTab.label,
              onClick: onShowVendorsHandler,
            },
            ...(ui.firstLayer.hideToggles
              ? []
              : [
                  {
                    ariaLabel: ui.buttons.manageSettings.label,
                    label: ui.buttons.manageSettings.label,
                    onClick: onCustomize,
                  },
                ]),
          ]}
          footer={{
            buttons: buttonsToShowInFirstLayer,
            showBorderBeforeFooter: true,
          }}
          header={{
            description: firstLayerDescription,
            enableHTMLTextFormatting: true,
            functions: {
              onClose: onCloseAmp,
              onFullScreenAmp,
              onLanguageChange,
            },
            showCloseButton: false,
            showFullScreenButton: isAmpEnabled && !isInFullScreen,
            showLanguageSelector: true,
            title: ui.firstLayer.title,
          }}
          layerSettings={{
            isOverlayEnabled: ui.firstLayer.isOverlayEnabled,
            layerPlacement: LAYER_PLACEMENT.CENTER,
            ...(isIE && { minVerticalScrollerHeight: 100 }),
            testId: testIds.firstLayer,
            withFadingBottom: false,
            withFadingTop: false,
          }}
          useBackgroundColor
        >
          <TCFFirstLayerContent
            categoriesTitle={ui.labels.general.nonIabPurposes}
            featuresTitle={ui.labels.general.features}
            hideNonIabPurposes={ui.firstLayer.hideNonIabPurposes}
            isVisible // should be always TRUE for TCF First Layer
            purposesTitle={ui.labels.general.purposes}
            useInlineToggle={!ui.firstLayer.showDescriptions}
            useFromStackOnly
          />
        </MainLayout>
      );
    case VIEW.SECOND_LAYER:
      return (
        <MainLayout
          footer={{ buttons: buttonsToShowInSecondLayer, showBorderBeforeFooter: true }}
          header={{
            description: ui.secondLayer.description,
            enableHTMLTextFormatting: true,
            functions: {
              onClose: onCloseTCFHandler,
              onLanguageChange,
            },
            showCloseButton: true,
            showLanguageSelector: true,
            title: ui.secondLayer.title,
          }}
          layerSettings={{
            isOverlayEnabled: ui.secondLayer.isOverlayEnabled,
            layerPlacement: LAYER_PLACEMENT.CENTER,
            ...(isIE && { minVerticalScrollerHeight: 350 }),
            testId: testIds.secondLayer,
            withFadingBottom: true,
          }}
          useBackgroundColor={false}
        >
          <Tabs>
            <TCFTabs />
          </Tabs>
        </MainLayout>
      );
    default:
      return <Fragment />;
  }
};

export default memo(TCF);
